import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import Switch from 'components/atoms/Switch'
import Modal from 'components/molecules/Modal'
import Table from 'components/pageComponents/Table'
import { Field, MethodBadge, NumberInput, StatusBadge, btn, fmtDate, isBlank, omr, toNumber } from 'pages/policy/refundShared'

/** Refund engine records for one booking + the P3 delivery-fee compensation action (admin only). */
const BookingRefundEngine = ({ bookingId }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'ar' ? 'ar' : 'en'
  const [refunds, setRefunds] = useState([])
  const [compensating, setCompensating] = useState(false)
  const [form, setForm] = useState({ wentToOffice: false })
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  const fetchRefunds = () => apiGet(`refunds/booking/${bookingId}`).then(response => response?.success && setRefunds(response.data || []))

  useEffect(() => {
    fetchRefunds()
  }, [bookingId])

  const openCompensate = () => {
    setForm({ wentToOffice: false })
    setError(null)
    setCompensating(true)
  }

  const compensate = async () => {
    if (isBlank(form.deliveryDelayMinutes)) {
      setError(t('refundEngine.common.required'))
      return
    }
    setSaving(true)
    const response = await apiPost(`refunds/compensate/${bookingId}`, { deliveryDelayMinutes: toNumber(form.deliveryDelayMinutes), wentToOffice: Boolean(form.wentToOffice) })
    setSaving(false)
    if (response?.success) {
      notify.success(t('refundEngine.booking.compensated', { amount: omr(get(response, 'data.refund.finalAmount')) }))
      setCompensating(false)
      fetchRefunds()
    }
  }

  const columns = [
    { name: t('refundEngine.refunds.columns.created'), rendered: r => <span className="text-gray-500">{fmtDate(r.createdAt)}</span> },
    { name: t('refundEngine.refunds.kind'), rendered: r => (r.kind ? t(`refundEngine.kind.${r.kind}`) : '-') },
    {
      name: t('refundEngine.refunds.columns.rule'),
      rendered: r => (
        <span>
          <span className="font-semibold">{r.ruleCode || '-'}</span> {r.ruleVersion ? <span className="text-xs text-gray-500">v{r.ruleVersion}</span> : ''}{' '}
          <span className="text-gray-500">{get(r, `ruleSnapshot.name.${lang}`, '')}</span>
        </span>
      ),
    },
    { name: t('refundEngine.refunds.columns.status'), rendered: r => <StatusBadge status={r.status} /> },
    { name: t('refundEngine.refunds.columns.method'), rendered: r => <MethodBadge method={r.method} /> },
    { name: t('refundEngine.refunds.columns.final'), rendered: r => <div className="text-right rtl:text-left font-semibold tabular-nums">{omr(r.finalAmount)}</div> },
  ]

  return (
    <>
      <div className="border-b border-slate-200/70 bg-white px-3 sm:px-5 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-sm font-semibold text-slate-800">{t('refundEngine.booking.refundsTitle')}</span>
          <button type="button" className={btn('secondary', true)} onClick={openCompensate}>
            {t('refundEngine.booking.compensate')}
          </button>
        </div>
        {refunds.length ? (
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table compact hasMore={false} fetchData={() => {}} data={refunds} columns={columns} />
          </div>
        ) : (
          <p className="text-xs text-slate-500">{t('refundEngine.booking.noRefunds')}</p>
        )}
      </div>

      <Modal
        open={compensating}
        closeable
        onClose={() => setCompensating(false)}
        title={t('refundEngine.booking.compensate')}
        description={t('refundEngine.booking.compensateHint')}
        dimensions={{ width: '92vw', maxWidth: '480px', height: 'auto' }}
      >
        <div className="space-y-4">
          <Field label={t('refundEngine.facts.deliveryDelayMinutes')} htmlFor="compensate-delay" error={error}>
            <NumberInput
              id="compensate-delay"
              min={0}
              value={form.deliveryDelayMinutes}
              error={error}
              onChange={v => {
                setForm(prev => ({ ...prev, deliveryDelayMinutes: v }))
                setError(null)
              }}
            />
          </Field>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-700">{t('refundEngine.facts.wentToOffice')}</span>
            <Switch value={Boolean(form.wentToOffice)} onChange={checked => setForm(prev => ({ ...prev, wentToOffice: checked }))} />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button type="button" className={btn('secondary')} onClick={() => setCompensating(false)} disabled={saving}>
              {t('refundEngine.common.cancel')}
            </button>
            <button type="button" className={btn('primary')} onClick={compensate} disabled={saving}>
              {saving ? t('common.saving') : t('refundEngine.common.confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default BookingRefundEngine
